import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { ArrowLeft, Save, Building2, Phone, Mail, User, CreditCard, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const FORM_INITIAL = {
  nome: '', nome_fantasia: '', cnpj: '', contato: '',
  telefone: '', email: '', comissao_padrao: '', observacoes: '',
};

const inp = "w-full border border-slate-200 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#0A3D73] focus:ring-2 focus:ring-[#0A3D73]/10 transition-all";
const lbl = "text-xs font-bold uppercase tracking-wide text-slate-400 mb-1 block";

const toNum = (v) => parseFloat(String(v || '').replace(/\./g, '').replace(',', '.')) || 0;
const fmt = (v, c = 2) => new Intl.NumberFormat('pt-BR', { minimumFractionDigits: c, maximumFractionDigits: c }).format(v || 0);

const maskCNPJ = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 14);
  if (d.length <= 2)  return d;
  if (d.length <= 5)  return `${d.slice(0,2)}.${d.slice(2)}`;
  if (d.length <= 8)  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5)}`;
  if (d.length <= 12) return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8)}`;
  return `${d.slice(0,2)}.${d.slice(2,5)}.${d.slice(5,8)}/${d.slice(8,12)}-${d.slice(12,14)}`;
};

const maskTelefone = (v) => {
  const d = v.replace(/\D/g, '').substring(0, 11);
  if (d.length === 0)  return '';
  if (d.length <= 2)   return `(${d}`;
  if (d.length <= 6)   return `(${d.slice(0,2)}) ${d.slice(2)}`;
  if (d.length <= 10)  return `(${d.slice(0,2)}) ${d.slice(2,6)}-${d.slice(6)}`;
  return `(${d.slice(0,2)}) ${d.slice(2,7)}-${d.slice(7,11)}`;
};

const RepresentadaForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const isEditing = Boolean(id);

  const [formData, setFormData] = useState(FORM_INITIAL);
  const [initialData, setInitialData] = useState(FORM_INITIAL);
  const [loading, setLoading] = useState(isEditing);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (isEditing) fetchRepresentada();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchRepresentada = async () => {
    try {
      const r = await api.get(`/representadas/${id}`);
      const rep = r.data;
      const data = {
        nome:            rep.nome            || '',
        nome_fantasia:   rep.nome_fantasia   || '',
        cnpj:            maskCNPJ(rep.cnpj   || ''),
        contato:         rep.contato         || '',
        telefone:        maskTelefone(rep.telefone || ''),
        email:           rep.email           || '',
        comissao_padrao: String(rep.comissao_padrao || '').replace('.', ','),
        observacoes:     rep.observacoes     || '',
      };
      setFormData(data);
      setInitialData(data);
    } catch {
      toast.error('Representada não encontrada');
      navigate('/representadas');
    } finally {
      setLoading(false);
    }
  };

  const isDirty = JSON.stringify(formData) !== JSON.stringify(initialData);

  const handleBack = () => {
    if (isDirty && !window.confirm('Há alterações não salvas. Deseja sair mesmo assim?')) return;
    navigate('/representadas');
  };

  const set = (field, value) => setFormData(prev => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isAdmin) return toast.error('Ação não permitida');
    if (!formData.nome.trim()) return toast.error('Nome é obrigatório');
    setSaving(true);
    try {
      const data = {
        nome:            formData.nome,
        nome_fantasia:   formData.nome_fantasia,
        cnpj:            formData.cnpj.replace(/\D/g, ''),
        contato:         formData.contato,
        telefone:        formData.telefone.replace(/\D/g, ''),
        email:           formData.email,
        comissao_padrao: toNum(formData.comissao_padrao),
        observacoes:     formData.observacoes,
      };
      if (isEditing) {
        await api.put(`/representadas/${id}`, data);
        toast.success('Representada atualizada!');
      } else {
        await api.post('/representadas', data);
        toast.success('Representada cadastrada!');
      }
      navigate('/representadas');
    } catch {
      toast.error('Erro ao salvar representada');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return (
    <div className="flex flex-col items-center justify-center h-[80vh] gap-4">
      <div className="w-10 h-10 border-4 border-slate-200 border-t-[#0A3D73] rounded-full animate-spin" />
      <p className="text-xs font-black uppercase tracking-widest text-slate-400 animate-pulse">Carregando Representada...</p>
    </div>
  );

  const comissaoNum = toNum(formData.comissao_padrao);

  return (
    <form id="representada-form" onSubmit={handleSubmit} className="min-h-screen bg-[#E9EEF2]">

      {/* HEADER */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="flex items-center justify-between px-6 py-4">
          <div className="flex items-center gap-4">
            <button type="button" onClick={handleBack}
              className="flex items-center gap-1.5 text-xs font-black uppercase text-slate-500 hover:text-[#0A3D73] transition-colors">
              <ArrowLeft size={14} /> Voltar
            </button>
            <div className="w-px h-5 bg-slate-200" />
            <div>
              <h1 className="text-base font-black text-slate-900">
                {isEditing
                  ? `Editar — ${formData.nome_fantasia || formData.nome || id}`
                  : 'Nova Representada'}
              </h1>
              <p className="text-[11px] text-slate-400 font-semibold">Cadastro de Representada</p>
            </div>
          </div>
          <button
            type="submit"
            form="representada-form"
            disabled={saving}
            className="flex items-center gap-2 bg-[#0A3D73] hover:bg-[#082D54] text-white text-xs font-black uppercase px-5 py-2.5 rounded-xl shadow-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed">
            <Save size={14} /> {saving ? 'Salvando...' : 'Salvar'}
          </button>
        </div>
      </div>

      {/* BODY */}
      <div className="p-6">
        <div className="grid grid-cols-1 lg:grid-cols-[3fr_2fr] gap-6 max-w-6xl mx-auto">

          {/* ── COL ESQUERDA ── */}
          <div className="space-y-6">

            {/* Card 01 · Identificação */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">01 · Identificação</p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className={lbl}>Razão Social *</label>
                  <input
                    value={formData.nome}
                    onChange={e => set('nome', e.target.value.toUpperCase())}
                    required className={inp} placeholder="RAZÃO SOCIAL DA EMPRESA" />
                </div>
                <div className="md:col-span-2">
                  <label className={lbl}>Nome Fantasia <span className="normal-case font-normal text-slate-300">(opcional — usado na exibição)</span></label>
                  <input
                    value={formData.nome_fantasia}
                    onChange={e => set('nome_fantasia', e.target.value.toUpperCase())}
                    className={inp} placeholder="Ex: EMPRESA MODELO" />
                </div>
                <div>
                  <label className={lbl}>CNPJ</label>
                  <input
                    value={formData.cnpj}
                    onChange={e => set('cnpj', maskCNPJ(e.target.value))}
                    className={inp} placeholder="00.000.000/0000-00" />
                </div>
                <div>
                  <label className={lbl}>Comissão Padrão %</label>
                  <input
                    value={formData.comissao_padrao}
                    onChange={e => set('comissao_padrao', e.target.value)}
                    className={`${inp} border-emerald-200 focus:border-emerald-500`}
                    placeholder="Ex: 3,5" />
                </div>
              </div>
            </div>

            {/* Card 02 · Contato */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">02 · Contato</p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className={lbl}>Nome do Responsável</label>
                  <input
                    value={formData.contato}
                    onChange={e => set('contato', e.target.value)}
                    className={inp} placeholder="Nome do contato" />
                </div>
                <div>
                  <label className={lbl}>Telefone</label>
                  <input
                    value={formData.telefone}
                    onChange={e => set('telefone', maskTelefone(e.target.value))}
                    className={inp} placeholder="(00) 00000-0000" />
                </div>
                <div>
                  <label className={lbl}>E-mail</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={e => set('email', e.target.value)}
                    className={inp} placeholder="contato@empresa.com" />
                </div>
              </div>
            </div>

            {/* Card 03 · Observações */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">03 · Observações</p>
              <textarea
                value={formData.observacoes}
                onChange={e => set('observacoes', e.target.value)}
                className={`${inp} resize-none`}
                rows={4}
                placeholder="Observações internas sobre a representada, acordos, condições especiais..." />
            </div>

          </div>

          {/* ── COL DIREITA ── */}
          <div className="space-y-6">

            {/* Card Resumo */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
              <p className="text-xs font-black uppercase tracking-widest text-[#0A3D73] border-b border-[#0A3D73]/20 pb-2 mb-4">Resumo</p>
              <div className="space-y-4">

                {(formData.nome_fantasia || formData.nome) ? (
                  <div className="bg-[#EFF6FF] rounded-xl p-4 text-center">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-[#0A3D73]/60 mb-1">
                      <Building2 size={10} className="inline mr-1" />Nome Fantasia
                    </p>
                    <p className="text-lg font-black text-[#0A3D73] break-words">
                      {formData.nome_fantasia || formData.nome}
                    </p>
                  </div>
                ) : (
                  <div className="bg-slate-50 rounded-xl p-4 text-center border border-dashed border-slate-200">
                    <p className="text-xs font-bold text-slate-400">Preencha a Razão Social</p>
                  </div>
                )}

                {comissaoNum > 0 && (
                  <div className="bg-emerald-50 rounded-xl p-4 text-center border border-emerald-100">
                    <p className="text-[10px] font-bold uppercase tracking-widest text-emerald-600/70 mb-1">
                      <TrendingUp size={10} className="inline mr-1" />Comissão Padrão
                    </p>
                    <p className="text-3xl font-black text-emerald-700">{fmt(comissaoNum)}%</p>
                  </div>
                )}

                {formData.cnpj && (
                  <div className="flex items-start gap-2.5">
                    <CreditCard size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">CNPJ</p>
                      <p className="text-sm font-black font-mono text-slate-700">{formData.cnpj}</p>
                    </div>
                  </div>
                )}

                {formData.contato && (
                  <div className="flex items-start gap-2.5">
                    <User size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Responsável</p>
                      <p className="text-sm font-bold text-slate-700">{formData.contato}</p>
                    </div>
                  </div>
                )}

                {formData.email && (
                  <div className="flex items-start gap-2.5">
                    <Mail size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">E-mail</p>
                      <p className="text-sm font-bold text-slate-700 truncate">{formData.email}</p>
                    </div>
                  </div>
                )}

                {formData.telefone && (
                  <div className="flex items-start gap-2.5">
                    <Phone size={14} className="text-slate-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-[10px] font-bold uppercase tracking-wide text-slate-400">Telefone</p>
                      <p className="text-sm font-bold text-slate-700">{formData.telefone}</p>
                    </div>
                  </div>
                )}

              </div>
            </div>

          </div>
        </div>
      </div>
    </form>
  );
};

export default RepresentadaForm;
